import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Check, ChevronDown } from "lucide-react";

import { Button } from "../ui/button";
import { declOfNum } from "../../lib/utils";
import { furnitureTypesObject, photoUrl } from "../../config";
import RoomPhotoSlider from "./RoomPhotoSlider";
import DesignPreviewScroller from "./DesignPreviewScroller";
import PrintTariffPaymentConditions from "./PrintTariffPaymentConditions";

export default function RoomCardVertical({
  room,
  roomPhotoUrl,
  photoIndex,
  slideDirection,
  onPrev,
  onNext,
  onPhotoClick,
  expandedFeatures,
  onToggleFeatures,
  onShowDescription,
  onBooking,
  onBook,
  datesSelected,
  onDesignVariants,
  accentColor = "#14b8a6",
  textColor,
}) {
  const [tariffsOpen, setTariffsOpen] = useState(false);
  const [photos, setPhotos] = useState([{ src: "/empty.jpg" }]);

  useEffect(() => {
    if (room.photos?.length > 0) {
      setPhotos(
        room.photos.map((p) => ({
          src: `${photoUrl}/${roomPhotoUrl}/${room.id}/${p}.jpg?v=${room?.photos_ver || 0}`,
          name: p,
        })),
      );
    }
  }, []);

  const bookingHandler = (e) => {
    console.log(
      "onBooking",
      room.hotel,
      room.id,
      e.target.dataset.tariff,
      e.target.dataset.food,
    );
    onBooking(
      room.hotel,
      room.id,
      e.target.dataset.tariff,
      e.target.dataset.food,
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all flex flex-col"
    >
      {/* Photo */}
      <RoomPhotoSlider
        images={photos}
        positions={{
          ...(room?.photos_positions || {}),
        }}
        currentIndex={photoIndex}
        direction={slideDirection}
        onPrev={onPrev}
        onNext={onNext}
        onPhotoClick={onPhotoClick}
        className={`${room?.separate ? "h-[166px]" : "h-64"} flex-shrink-0`}
      >
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-semibold z-10">
          {room.hotels_space} м²
        </div>
      </RoomPhotoSlider>

      {/* Design variants strip */}
      {room?.separate && (
        <div
          className="px-3 py-2 bg-gray-50 border-b border-gray-100 cursor-pointer hover:bg-teal-50 transition-colors flex-shrink-0 overflow-hidden"
          style={{ height: "90px" }}
          onClick={onDesignVariants}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = `${accentColor}18`)
          }
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Варианты дизайна номера
            </p>
            <span
              className="text-xs text-teal-500 font-medium"
              style={{ color: textColor }}
            >
              Посмотреть →
            </span>
          </div>
          <DesignPreviewScroller
            imgClassName="w-[77px] h-[58px]"
            gap="gap-1.5"
            room={room}
            roomPhotoUrl={roomPhotoUrl}
            accentColor={accentColor}
            textColor={textColor}
          />
        </div>
      )}

      {/* Fixed header: room name + red divider */}
      <div className="px-6 pt-5 flex-shrink-0 bg-white">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{room.name}</h3>
      </div>

      {/* Scrollable body — relative container for tariff panel overlay */}
      <div className="flex flex-col flex-1 relative overflow-hidden">
        {/* Main content */}
        <div className="px-6 pt-1 pb-6 flex flex-col flex-1">
          {room?.beds && <p className="text-gray-600 mb-1">{room.beds}</p>}
          {room.additional_terms?.place_types?.length > 0 && (
            <p className="text-gray-500 text-sm mb-4">
              +
              {declOfNum(room.additional_terms.place_types.length, [
                "доп.место",
                "доп.места",
                "доп.мест",
              ])}{" "}
              –{" "}
              {[...new Set(room.additional_terms.place_types)]
                .map((type) => furnitureTypesObject[type].toLowerCase())
                .filter(Boolean)
                .join(", ")}
            </p>
          )}

          {room?.room_capacity && (
            <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
              <Users className="w-4 h-4" />
              <span>
                До{" "}
                {declOfNum(
                  room.room_capacity +
                    (room?.additional_terms?.places?.length || 0),
                  ["гостя", "гостей", "гостей"],
                )}
              </span>
            </div>
          )}

          <div className="space-y-2 mb-6">
            {room.services?.length > 0 &&
              room.services.slice(0, 4).map((feature, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-sm text-gray-600"
                >
                  <Check
                    className="w-4 h-4 text-teal-600 flex-shrink-0"
                    style={{ color: textColor }}
                  />
                  <span>{feature}</span>
                </div>
              ))}
            {expandedFeatures &&
              room.services?.slice(4).map((feature, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-sm text-gray-600"
                >
                  <Check
                    className="w-4 h-4 text-teal-600 flex-shrink-0"
                    style={{ color: textColor }}
                  />
                  <span>{feature}</span>
                </div>
              ))}
            {room.services?.length > 4 && (
              <button
                onClick={onToggleFeatures}
                className="text-sm text-teal-600 font-medium hover:text-teal-700 transition-colors text-left"
                style={{ color: textColor }}
              >
                {expandedFeatures
                  ? "Скрыть"
                  : `+${declOfNum(room.services.length - 4, ["удобство", "удобства", "удобств"])}`}
              </button>
            )}
          </div>

          <div className="mt-auto">
            <button
              onClick={() => onShowDescription(room)}
              className="text-sm text-black font-medium underline text-center w-full mb-3"
            >
              Подробное описание номера
            </button>

            {room?.tariffs?.prices?.[0]?.total_cost ? (
              <div className="space-y-2">
                <div className="flex items-baseline gap-2 flex-wrap justify-center">
                  <span className="text-sm text-gray-500 firstUp">
                    {room.tariffs.prices[0].food_type}
                  </span>
                  <span className="text-xl font-bold text-gray-900">
                    {room.tariffs.prices[0].total_cost} ₽
                  </span>
                  <span className="text-sm text-gray-500">
                    за {room.tariffs.days_count}{" "}
                    {room.tariffs.days_count === 1
                      ? "ночь"
                      : room.tariffs.days_count < 5
                        ? "ночи"
                        : "ночей"}
                  </span>
                </div>
                <Button
                  onClick={bookingHandler}
                  data-food={room.tariffs.prices[0].food_type}
                  data-tariff="base"
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
                  style={{
                    background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)`,
                    color: textColor,
                  }}
                >
                  Забронировать
                </Button>
                {room.price_variants_counter > 1 && (
                  <button
                    onClick={() => setTariffsOpen((v) => !v)}
                    className="w-full flex items-center justify-center gap-1 text-black-600 text-sm font-medium transition-colors"
                    // style={{ color: textColor }}
                  >
                    Ещё{" "}
                    {declOfNum(room.price_variants_counter - 1, [
                      "вариант",
                      "варианта",
                      "вариантов",
                    ])}{" "}
                    цены
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${tariffsOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                )}
              </div>
            ) : (
              !datesSelected &&
              room?.available_periods && (
                <Button
                  onClick={() => onBook(room)}
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
                  style={{
                    background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)`,
                    color: textColor,
                  }}
                >
                  Посмотреть свободные даты
                </Button>
              )
            )}
          </div>
        </div>

        {/* Tariff panel — slides up from bottom, covers everything below the red line */}
        <AnimatePresence>
          {tariffsOpen && (
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="absolute inset-0 bg-white z-10 flex flex-col"
            >
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gray-50 flex-shrink-0">
                <div className="font-semibold text-gray-900 text-sm">
                  Выберите тариф
                </div>
                <button
                  onClick={() => setTariffsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
                >
                  ×
                </button>
              </div>

              {/* Tariff rows */}
              <div className="overflow-y-auto flex-1">
                {room.tariffs?.variants?.length > 0 &&
                  room.tariffs.variants.map((v, ii) => {
                    if (!v.prices?.length) {
                      return null;
                    }
                    return (
                      <div className="bg-red-50" key={v.id}>
                        <div className="px-4 pt-2 pb-0.5">
                          <div className="text-xs text-gray-500 font-medium mb-0.5 text-right">
                            {v.name}
                          </div>
                          <div className="text-xs leading-snug mb-1 whitespace-pre-line text-left">
                            <PrintTariffPaymentConditions tariff={v} />
                          </div>
                        </div>
                        {v.prices.map((item) => (
                          <div
                            key={item.food_type || item.id}
                            className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors"
                          >
                            <span className="text-sm text-gray-800 firstUp">
                              {item.food_type}
                            </span>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-sm font-semibold text-gray-900">
                                {item.total_cost.toLocaleString("ru-RU")} ₽
                              </span>
                              <Button
                                size="sm"
                                data-tariff={v.id}
                                data-food={item.food_type}
                                onClick={bookingHandler}
                                className="h-7 px-3 text-xs bg-teal-500 hover:bg-teal-600 text-white"
                                style={{
                                  background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)`,
                                  color: textColor,
                                }}
                              >
                                Забронировать
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                <div className="border-t border-gray-100">
                  <div className="px-4 pt-2 pb-0.5">
                    <div className="text-xs text-gray-500 font-medium mb-0.5 text-right">
                      Базовый тариф
                    </div>
                    <div className="text-xs leading-snug mb-1 whitespace-pre-line text-left">
                      <PrintTariffPaymentConditions tariff={room.tariffs} />
                    </div>
                  </div>
                  {room.tariffs?.prices?.length > 0 &&
                    room.tariffs.prices.map((item, ii) => {
                      return (
                        <div
                          key={ii}
                          className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm text-gray-800 firstUp">
                            {item.food_type}
                          </span>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-sm font-semibold text-gray-900">
                              {item.total_cost.toLocaleString("ru-RU")} ₽
                            </span>
                            <Button
                              size="sm"
                              data-tariff="base"
                              data-food={item.food_type}
                              onClick={bookingHandler}
                              className="h-7 px-3 text-xs bg-teal-500 hover:bg-teal-600 text-white"
                              style={{
                                background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)`,
                                color: textColor,
                              }}
                            >
                              Забронировать
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Close button at bottom */}
              <div className="px-4 py-3 border-t flex-shrink-0">
                <button
                  onClick={() => setTariffsOpen(false)}
                  className="w-full flex items-center justify-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium"
                  style={{ color: accentColor }}
                >
                  Скрыть
                  <ChevronDown className="w-4 h-4 rotate-180" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
